import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Gauge, RefreshCw, Loader, TrendingUp, TrendingDown, CheckCircle,
  AlertTriangle, SlidersHorizontal, Target
} from 'lucide-react';
import { apiCall } from '../utils/api';
import { useTheme } from '../context/ThemeContext';
import { useToast } from './Toast';

// Pacing status from spend/budget ratio (%)
function pacingStatus(pacing) {
  if (pacing >= 110) return { key: 'over', label: 'Over-pacing', color: '#ef4444' };
  if (pacing < 80) return { key: 'under', label: 'Under-pacing', color: '#f59e0b' };
  return { key: 'on', label: 'On track', color: '#10b981' };
}

const fmt = (n, d = 0) => {
  const v = Number(n) || 0;
  return v.toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
};

export default function BudgetPacing({ selectedAccount, embedded = false, initialTab = 'pacing' }) {
  const { theme } = useTheme();
  const { showToast } = useToast();

  const [campaigns, setCampaigns] = useState([]);
  const [summary, setSummary] = useState(null);
  const [days, setDays] = useState(30);
  const [respDays, setRespDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(initialTab);
  const [selectedId, setSelectedId] = useState(null);
  const [sliderBudget, setSliderBudget] = useState(0);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    setTab(initialTab);
  }, [initialTab]);

  const fetchData = useCallback(async () => {
    if (!selectedAccount) return;
    setLoading(true);
    try {
      const response = await apiCall(`/api/budget-allocator/analyze?days=${days}`, { timeout: 50000 });
      const data = await response.json();
      if (data.success) {
        const list = data.campaigns || [];
        setCampaigns(list);
        setSummary(data.summary || null);
        setRespDays(data.days || days);
        setSelectedId(prev => {
          if (prev && list.some(c => c.id === prev)) return prev;
          // default: the budget-limited Tier 1 campaign, else the biggest spender
          const limited = list.find(c => c.tier === 1 && c.budgetLostIS > 5);
          const fallback = [...list].sort((a, b) => b.totalCost - a.totalCost)[0];
          return (limited || fallback)?.id || null;
        });
      } else {
        throw new Error(data.error || 'Analysis failed');
      }
    } catch (err) {
      showToast(`Failed to analyze budgets: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedAccount, days, showToast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const selected = useMemo(
    () => campaigns.find(c => c.id === selectedId) || null,
    [campaigns, selectedId]
  );

  // Initialise the slider when the selected campaign changes
  useEffect(() => {
    if (selected) setSliderBudget(Math.round(selected.dailyBudget || selected.dailySpend || 10));
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Transparent projection model. Scaling down is linear with spend. Scaling up
  // raises spend toward a "demand ceiling" derived from budget-lost impression
  // share (preferred) or, when that metric is unavailable, from how fully the
  // campaign currently uses its budget (pacing). Incremental spend converts at a
  // diminishing rate.
  const projection = useMemo(() => {
    if (!selected) return null;
    const curSpend = selected.dailySpend || 0;
    const curBudget = selected.dailyBudget || 0;
    const curConv = (selected.conversions || 0) / respDays;
    const curValue = (selected.conversionValue || 0) / respDays;
    const convPerEuro = curSpend > 0 ? curConv / curSpend : 0;
    const valuePerEuro = curSpend > 0 ? curValue / curSpend : 0;
    const nb = sliderBudget;

    // Estimate the most this campaign could realistically spend per day.
    const lost = Math.min(0.9, (selected.budgetLostIS || 0) / 100);
    const pacingFrac = curBudget > 0 ? curSpend / curBudget : 1;
    let ceiling;
    if (lost > 0) {
      ceiling = curSpend / Math.max(0.1, 1 - lost); // recover the lost share
    } else if (pacingFrac >= 0.9) {
      ceiling = curSpend * 1.5; // spends its full budget → assume some headroom
    } else {
      ceiling = curSpend; // not even using current budget → extra won't be spent
    }

    let dailySpend, dailyConv, dailyValue;
    if (nb <= curSpend) {
      const f = curSpend > 0 ? nb / curSpend : 0;
      dailySpend = nb; dailyConv = curConv * f; dailyValue = curValue * f;
    } else {
      const eff = 0.75; // incremental spend converts a bit less efficiently
      dailySpend = Math.min(nb, ceiling);
      const extraSpend = Math.max(0, dailySpend - curSpend);
      dailyConv = curConv + extraSpend * convPerEuro * eff;
      dailyValue = curValue + extraSpend * valuePerEuro * eff;
    }
    return {
      current: {
        spend: curSpend * 30,
        conv: curConv * 30,
        value: curValue * 30,
        cpa: curConv > 0 ? curSpend / curConv : null,
        roas: curSpend > 0 ? curValue / curSpend : null
      },
      projected: {
        spend: dailySpend * 30,
        conv: dailyConv * 30,
        value: dailyValue * 30,
        cpa: dailyConv > 0 ? dailySpend / dailyConv : null,
        roas: dailySpend > 0 ? dailyValue / dailySpend : null
      }
    };
  }, [selected, sliderBudget, respDays]);

  const applyBudget = async () => {
    if (!selected) return;
    setApplying(true);
    try {
      const response = await apiCall('/api/budget-allocator/apply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ campaignId: selected.id, newBudget: sliderBudget }),
        timeout: 35000
      });
      const data = await response.json();
      if (data.success) {
        showToast(`Budget set to €${sliderBudget}/day for "${selected.name}"`, 'success');
        setCampaigns(prev => prev.map(c => c.id === selected.id ? { ...c, dailyBudget: sliderBudget } : c));
      } else {
        throw new Error(data.error || 'Failed to apply');
      }
    } catch (err) {
      showToast(`Failed: ${err.message}`, 'error');
    } finally {
      setApplying(false);
    }
  };

  const t = theme.colors;
  const card = {
    background: t.bg.secondary,
    border: `1px solid ${t.border.primary}`,
    borderRadius: 16,
    padding: 20
  };

  const tabBtn = (active) => ({
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '10px 18px', borderRadius: 10, cursor: 'pointer',
    border: `1px solid ${active ? 'transparent' : t.border.primary}`,
    background: active ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' : 'transparent',
    color: active ? '#fff' : t.text.secondary,
    fontSize: 14, fontWeight: 600
  });

  const controls = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <select
        value={days}
        onChange={e => setDays(parseInt(e.target.value))}
        style={{
          background: t.bg.secondary, color: t.text.primary,
          border: `1px solid ${t.border.primary}`, borderRadius: 10,
          padding: '9px 12px', fontSize: 14
        }}
      >
        <option value={7}>Last 7 days</option>
        <option value={14}>Last 14 days</option>
        <option value={30}>Last 30 days</option>
      </select>
      <button
        onClick={fetchData}
        disabled={loading}
        style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '9px 16px',
          borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 600,
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', color: '#fff'
        }}
      >
        {loading ? <Loader size={16} className="spin" /> : <RefreshCw size={16} />} Refresh
      </button>
    </div>
  );

  return (
    <div style={{ padding: embedded ? 0 : 40, maxWidth: embedded ? 'none' : 1400, margin: embedded ? 0 : '0 auto', color: t.text.primary }}>
      {/* Header */}
      {embedded ? (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          {controls}
        </div>
      ) : (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16, marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: 28, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
              <Gauge size={26} style={{ color: '#8b5cf6' }} /> Budget Pacing & Planner
            </h1>
            <p style={{ color: t.text.tertiary, margin: '6px 0 0', fontSize: 14 }}>
              See which campaigns are on track, and model what happens when you change a budget.
            </p>
          </div>
          {controls}
        </div>
      )}

      {/* Tabs */}
      {!embedded && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 24 }}>
          <div style={tabBtn(tab === 'pacing')} onClick={() => setTab('pacing')}>
            <Gauge size={16} /> Pacing
          </div>
          <div style={tabBtn(tab === 'planner')} onClick={() => setTab('planner')}>
            <SlidersHorizontal size={16} /> Scenario planner
          </div>
        </div>
      )}

      {loading && campaigns.length === 0 ? (
        <div style={{ ...card, textAlign: 'center', padding: 60, color: t.text.tertiary }}>
          <Loader size={32} className="spin" style={{ marginBottom: 12 }} />
          <div>Analyzing campaign budgets…</div>
        </div>
      ) : campaigns.length === 0 ? (
        <div style={{ ...card, textAlign: 'center', padding: 60, color: t.text.tertiary }}>
          No active campaigns with budget data found for this period.
        </div>
      ) : tab === 'pacing' ? (
        <>
          {/* Summary cards */}
          {summary && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
              {[
                { label: 'Overall pacing', value: `${summary.overallPacing}%`, sub: pacingStatus(summary.overallPacing).label, color: pacingStatus(summary.overallPacing).color },
                { label: 'Daily budget', value: `€${fmt(summary.totalDailyBudget)}`, sub: 'across all campaigns' },
                { label: 'Daily spend', value: `€${fmt(summary.totalDailySpend)}`, sub: `${respDays}-day average` },
                { label: 'Campaigns', value: fmt(summary.campaignCount), sub: `${summary.tiers?.tier1 || 0} Tier 1` }
              ].map((s, i) => (
                <div key={i} style={card}>
                  <div style={{ color: t.text.tertiary, fontSize: 13, marginBottom: 6 }}>{s.label}</div>
                  <div style={{ fontSize: 26, fontWeight: 700, color: s.color || t.text.primary }}>{s.value}</div>
                  <div style={{ color: t.text.tertiary, fontSize: 12, marginTop: 4 }}>{s.sub}</div>
                </div>
              ))}
            </div>
          )}

          {/* Pacing list */}
          <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
            {[...campaigns].sort((a, b) => b.dailyBudget - a.dailyBudget).map((c, i, arr) => {
              const st = pacingStatus(c.pacing);
              const pct = Math.min(100, c.pacing);
              return (
                <div key={c.id} style={{
                  padding: '16px 20px',
                  borderBottom: i < arr.length - 1 ? `1px solid ${t.border.secondary}` : 'none',
                  display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap'
                }}>
                  <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                    <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name}</div>
                    <div style={{ color: t.text.tertiary, fontSize: 12, marginTop: 2 }}>
                      €{fmt(c.dailySpend, 0)}/day spend · €{fmt(c.dailyBudget, 0)}/day budget
                      {c.budgetLostIS > 5 && <span style={{ color: '#f59e0b' }}> · {c.budgetLostIS}% IS lost to budget</span>}
                    </div>
                  </div>
                  {/* pacing bar */}
                  <div style={{ flex: '1 1 200px', minWidth: 140 }}>
                    <div style={{ height: 8, borderRadius: 6, background: t.bg.hover, overflow: 'hidden' }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: st.color, borderRadius: 6 }} />
                    </div>
                  </div>
                  <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: st.color, minWidth: 48, textAlign: 'right' }}>{c.pacing}%</span>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      padding: '4px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600,
                      background: `${st.color}1f`, color: st.color
                    }}>
                      {st.key === 'on' ? <CheckCircle size={13} /> : st.key === 'over' ? <TrendingUp size={13} /> : <TrendingDown size={13} />}
                      {st.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        /* ===== Scenario planner ===== */
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(280px, 1fr) minmax(280px, 1.4fr)', gap: 20, alignItems: 'start' }}>
          {/* Controls */}
          <div style={card}>
            <label style={{ color: t.text.tertiary, fontSize: 13, display: 'block', marginBottom: 8 }}>Campaign</label>
            <select
              value={selectedId || ''}
              onChange={e => setSelectedId(e.target.value)}
              style={{
                width: '100%', background: t.bg.secondary, color: t.text.primary,
                border: `1px solid ${t.border.primary}`, borderRadius: 10, padding: '10px 12px', fontSize: 14
              }}
            >
              {campaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>

            {selected && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', margin: '24px 0 6px' }}>
                  <span style={{ color: t.text.tertiary, fontSize: 13 }}>Daily budget</span>
                  <span style={{ fontSize: 24, fontWeight: 700, color: '#8b5cf6' }}>€{fmt(sliderBudget)}</span>
                </div>
                <input
                  type="range"
                  min={1}
                  max={Math.max(20, Math.round((selected.dailyBudget || selected.dailySpend || 10) * 3))}
                  step={1}
                  value={sliderBudget}
                  onChange={e => setSliderBudget(parseInt(e.target.value))}
                  style={{ width: '100%', accentColor: '#8b5cf6' }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', color: t.text.tertiary, fontSize: 11, marginTop: 4 }}>
                  <span>€1</span>
                  <span>Current: €{fmt(selected.dailyBudget)}</span>
                  <span>€{fmt(Math.max(20, Math.round((selected.dailyBudget || selected.dailySpend || 10) * 3)))}</span>
                </div>

                <button
                  onClick={applyBudget}
                  disabled={applying || sliderBudget === Math.round(selected.dailyBudget)}
                  style={{
                    width: '100%', marginTop: 22, padding: '11px 16px', borderRadius: 10, border: 'none',
                    cursor: applying || sliderBudget === Math.round(selected.dailyBudget) ? 'not-allowed' : 'pointer',
                    fontWeight: 600, color: '#fff',
                    opacity: applying || sliderBudget === Math.round(selected.dailyBudget) ? 0.5 : 1,
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8
                  }}
                >
                  {applying ? <Loader size={16} className="spin" /> : <Target size={16} />}
                  Apply €{fmt(sliderBudget)}/day budget
                </button>
              </>
            )}
          </div>

          {/* Projection */}
          {projection && (
            <div style={card}>
              <h3 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 600 }}>Projected monthly outcome</h3>
              <p style={{ color: t.text.tertiary, fontSize: 12, margin: '0 0 20px' }}>
                Estimate based on budget-lost impression share and current conversion rate. Actual results vary.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                {[
                  { label: 'Spend', cur: `€${fmt(projection.current.spend)}`, proj: `€${fmt(projection.projected.spend)}` },
                  { label: 'Conversions', cur: fmt(projection.current.conv, 1), proj: fmt(projection.projected.conv, 1) },
                  { label: 'CPA', cur: projection.current.cpa ? `€${fmt(projection.current.cpa, 2)}` : '—', proj: projection.projected.cpa ? `€${fmt(projection.projected.cpa, 2)}` : '—' },
                  { label: 'ROAS', cur: projection.current.roas ? `${fmt(projection.current.roas, 2)}x` : '—', proj: projection.projected.roas ? `${fmt(projection.projected.roas, 2)}x` : '—' }
                ].map((m, i) => (
                  <div key={i} style={{ background: t.bg.hover, borderRadius: 12, padding: 14 }}>
                    <div style={{ color: t.text.tertiary, fontSize: 12, marginBottom: 8 }}>{m.label}</div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ color: t.text.tertiary, fontSize: 13, textDecoration: 'line-through' }}>{m.cur}</span>
                      <span style={{ fontSize: 20, fontWeight: 700 }}>{m.proj}</span>
                    </div>
                  </div>
                ))}
              </div>
              {selected && (() => {
                const increasing = sliderBudget > (selected.dailySpend || 0);
                const projDaily = projection.projected.spend / 30;
                const capped = increasing && projDaily < sliderBudget - 0.5;
                if (selected.budgetLostIS > 5 && increasing) {
                  return (
                    <div style={{
                      marginTop: 16, display: 'flex', gap: 8, alignItems: 'flex-start',
                      background: '#f59e0b14', borderRadius: 10, padding: 12, color: t.text.secondary, fontSize: 13
                    }}>
                      <AlertTriangle size={16} style={{ color: '#f59e0b', flex: '0 0 auto', marginTop: 1 }} />
                      This campaign loses {selected.budgetLostIS}% impression share to its budget — there's room to capture more volume.
                    </div>
                  );
                }
                if (capped) {
                  return (
                    <div style={{
                      marginTop: 16, display: 'flex', gap: 8, alignItems: 'flex-start',
                      background: t.bg.hover, borderRadius: 10, padding: 12, color: t.text.secondary, fontSize: 13
                    }}>
                      <AlertTriangle size={16} style={{ color: t.text.tertiary, flex: '0 0 auto', marginTop: 1 }} />
                      This campaign already spends within its budget, so it likely won't use the full €{fmt(sliderBudget)}/day — extra budget mostly helps budget-limited campaigns.
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          )}
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } } .spin { animation: spin 1s linear infinite; }`}</style>
    </div>
  );
}
